import { useEffect, useMemo, useRef, useState } from "react";
import { Calendar, Clock3, Flame, HelpCircle, Ruler, Zap } from "lucide-react";
import { SHEETS } from "./api/sheets";
import { parseCSV } from "./utils/csv";
import { getRecommendedOptions, resolvePrice } from "./utils/pricingEngine";
import { buildLeadPayload } from "./utils/leadPayload";
import { applySheetInstructionRules } from "./utils/sheetInstructions";
import {
  getTrackingPayload,
  getVisitorContext,
  type VisitorContext,
} from "./utils/tracking";

import { StickyHeaderProgress } from "./components/Progress";
import { Result } from "./components/Result";
import { VisualStep } from "./components/VisualStep";
import { LargeCardStep } from "./components/LargeCardStep";
import { ZipCodeStep } from "./components/ZipCodeStep";
import { DetailsFormStep } from "./components/DetailsFormStep";
import { FakeProcessing } from "./components/FakeProcessing";
import { TonnageSelectStep } from "./components/TonnageSelectStep";
import { MiniSplitRoomsIcon } from "./components/MiniSplitRoomsIcon";
import { AtticIcon } from "./components/AtticIcon";
import { EstimatePage } from "./components/EstimatePage";

import { steps } from "./utils/steps";
import { resolveAppCopy } from "./utils/contentCopy";

const STORAGE_KEY = "hvac-quote-progress-v1";
const PROGRESS_TTL_MS = 2 * 24 * 60 * 60 * 1000;
const ZAPIER_WEBHOOK_URL =
  "https://hooks.zapier.com/hooks/catch/9742130/up8sa97/";
const THANK_YOU_URL =
  "https://bakerhomeenergy.com/heating-and-cooling/estimate-thank-you/";
const RENT_NOTICE =
  "Please note, we’ll need notice of confirmation from the homeowner before moving forward with any estimates.";
const SEER_INFO_TEXT =
  "SEER (Seasonal Energy Efficiency Ratio) measures how energy-efficient an air conditioner is. The higher the SEER number, the less electricity it uses to cool your home, which will lower your energy bills.";
const ENERGY_RATE_CENTS_PER_KWH = 43.5;
const COOLING_HOURS_PER_YEAR = 1000;

const SEER_COEFFICIENTS: Record<number, number> = {
  4: 1.87706666666667,
  5: 1.76613333333333,
  6: 1.6552,
  7: 1.54426666666667,
  8: 1.43333333333333,
  9: 1.3333333333333333,
  10: 1.2,
  11: 1.0906666666666667,
  12: 1,
  13: 0.9226666666666666,
  14: 0.856,
  15: 0.8,
  16: 0.7493333333333334,
  17: 0.7066666666666667,
  18: 0.6666666666666666,
  19: 0.632,
  20: 0.6,
  21: 0.5706666666666667,
  22: 0.5466666666666666,
  23: 0.5226666666666666,
  24: 0.5013333333333333,
  25: 0.48,
  26: 0.46,
};

const SEER_RANGES: Record<string, { label: string; estimateSeer: number }> = {
  good: { label: "15-18 SEER", estimateSeer: 16.5 },
  better: { label: "18-21 SEER", estimateSeer: 19.5 },
  best: { label: "21-25 SEER", estimateSeer: 23 },
  miniSplit: { label: "15-25 SEER", estimateSeer: 20 },
};

const AFUE_RANGES: Record<string, string> = {
  good: "80% AFUE",
  better: "90%+ AFUE",
  best: "96%+ AFUE",
};

// Address fields are temporarily disabled to streamline the estimator flow.
// AI AGENT NOTE:
// If client asks to add address back, restore the address field list below and
// uncomment the address render + validation blocks marked in this file.
//
// const HOME_ADDRESS_FIELDS = [
//   { key: "addressStreet", label: "Street", autoComplete: "address-line1" },
//   { key: "addressCity", label: "City", autoComplete: "address-level2" },
//   { key: "addressState", label: "State", autoComplete: "address-level1" },
//   { key: "addressZip", label: "Zip", autoComplete: "postal-code" },
// ];

const CONTACT_FIELDS: Array<{
  key: string;
  label: string;
  autoComplete?: string;
  type?: "text" | "email" | "tel";
}> = [
  { key: "contactFirstName", label: "First Name", autoComplete: "given-name" },
  { key: "contactLastName", label: "Last Name", autoComplete: "family-name" },
  { key: "contactEmail", label: "Email", type: "email" as const, autoComplete: "email" },
  { key: "contactPhone", label: "Phone", type: "tel" as const, autoComplete: "tel" },
];

type SavedProgress = {
  page?: number;
  pageId?: number;
  lead: Record<string, any>;
};

type OptionMetric = {
  label: string;
  value: string;
  info?: string;
};

type EnergySavingsEstimate = {
  currentSeer: number;
  newSeer: number;
  seerRange: string;
  tonnage: string;
  currentAnnualCost: number;
  newAnnualCost: number;
  annualSavings: number;
  percentSavings: number;
};

function normalizeForMatch(value: unknown) {
  return String(value ?? "")
    .toLowerCase()
    .replace(/["']/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function onlyDigits(value: string) {
  return value.replace(/\D/g, "");
}

function parseNumber(value: unknown) {
  const match = String(value ?? "").match(/-?\d+(\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatPhone(value: string) {
  const digits = onlyDigits(value).slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function formatZip(value: string) {
  const digits = onlyDigits(value).slice(0, 9);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function hasValueMatch(haystack: unknown, needle?: string) {
  if (!needle) return false;
  const normalizedHaystack = normalizeForMatch(haystack);
  const normalizedNeedle = normalizeForMatch(needle);
  return normalizedHaystack.includes(normalizedNeedle);
}

function isFurnaceOnly(value: unknown) {
  return /furnace only|funace only/i.test(normalizeForMatch(value));
}

function isRent(value: unknown) {
  return normalizeForMatch(value).includes("rent");
}

function isUnknownChoice(value: unknown) {
  const normalized = normalizeForMatch(value);
  return (
    !normalized ||
    normalized.includes("dont know") ||
    normalized.includes("don't know") ||
    normalized.includes("not sure") ||
    normalized.includes("dont have ac") ||
    normalized.includes("don't have ac") ||
    normalized.includes("no ac")
  );
}

function resolveDesiredFuelType(lead: Record<string, any>) {
  if (!isUnknownChoice(lead.desiredFuelType)) return lead.desiredFuelType;
  if (!isUnknownChoice(lead.currentFuelType)) return lead.currentFuelType;
  return "Gas";
}

function inferTonnageFromSquareFeet(lead: Record<string, any>, squareFeetRows: any[]) {
  if (!isUnknownChoice(lead.currentACtonnage)) return lead.currentACtonnage;
  if (!lead.squareFeet || !Array.isArray(squareFeetRows)) return lead.currentACtonnage;

  const selectedSquareFeet = normalizeForMatch(lead.squareFeet);
  const matchedRow = squareFeetRows.find((row: Record<string, any>) => {
    const firstValue = Object.values(row)[0];
    return normalizeForMatch(firstValue) === selectedSquareFeet;
  });

  if (!matchedRow) return lead.currentACtonnage;

  const values = Object.values(matchedRow)
    .map((v) => String(v ?? "").trim())
    .filter(Boolean);

  for (let i = values.length - 1; i >= 0; i--) {
    const candidate = values[i].replace(/,/g, "");
    if (/^\d+(\.\d+)?$/.test(candidate)) {
      return values[i];
    }
  }

  return lead.currentACtonnage;
}

function getProductImageSystemType(systemType: unknown) {
  const normalized = normalizeForMatch(systemType);
  if (normalized.includes("mini")) return "minisplit";
  if (normalized.includes("furnace")) return "furnaceonly";
  if (normalized.includes("ac only")) return "aconly";
  if (normalized.includes("cooling") || normalized.includes("central")) {
    return "centralheatairsystem";
  }
  return "";
}

function getProductImageUrl(systemType: unknown, imageRows: any[] = []) {
  const targetSystemType = getProductImageSystemType(systemType);
  if (!targetSystemType || !Array.isArray(imageRows)) return null;

  const match = imageRows.find((row) => {
    const systemTypeValue =
      row["System Type"] ?? row.systemType ?? row.SystemType ?? Object.values(row)[0];
    return normalizeForMatch(systemTypeValue) === targetSystemType;
  });

  if (!match) return null;

  const url = match.URL ?? match.url ?? match.Image ?? match.imageUrl ?? Object.values(match)[1];
  const normalizedUrl = String(url ?? "").trim();
  return normalizedUrl || null;
}

function getPackageTier(option: any) {
  const normalizedTitle = normalizeForMatch(option?.title);
  if (normalizedTitle.includes("good") || normalizedTitle.includes("lower")) return "good";
  if (normalizedTitle.includes("better")) return "better";
  if (normalizedTitle.includes("best") || normalizedTitle.includes("upper")) return "best";
  return "good";
}

function getCurrentSeerRating(lead: Record<string, any>) {
  const directSeer = parseNumber(lead.assumedSeerRating);
  if (directSeer) return directSeer;

  const age = normalizeForMatch(lead.currentSystemAge);
  if (age.includes("0-5")) return 16;
  if (age.includes("5-10")) return 14;
  if (age.includes("10-15")) return 12;
  if (age.includes("15-20")) return 10;
  if (age.includes("20+")) return 8;
  return null;
}

function getSeerCoefficient(seer: number) {
  if (Number.isInteger(seer) && SEER_COEFFICIENTS[seer] !== undefined) {
    return SEER_COEFFICIENTS[seer];
  }

  const seerValues = Object.keys(SEER_COEFFICIENTS).map(Number).sort((a, b) => a - b);
  const minSeer = seerValues[0];
  const maxSeer = seerValues[seerValues.length - 1];
  const clampedSeer = Math.max(minSeer, Math.min(maxSeer, seer));
  const lower = Math.floor(clampedSeer);
  const upper = Math.ceil(clampedSeer);

  if (lower === upper) return SEER_COEFFICIENTS[lower];

  const lowerCoefficient = SEER_COEFFICIENTS[lower];
  const upperCoefficient = SEER_COEFFICIENTS[upper];
  const ratio = clampedSeer - lower;
  return lowerCoefficient + (upperCoefficient - lowerCoefficient) * ratio;
}

function getAnnualCoolingCost(tonnage: number, seer: number) {
  const coefficient = getSeerCoefficient(seer);
  return COOLING_HOURS_PER_YEAR * coefficient * tonnage * (ENERGY_RATE_CENTS_PER_KWH / 100);
}

function getSeerSavings(lead: Record<string, any>, newSeer: number) {
  const tonnage = parseNumber(lead.currentACtonnage);
  const currentSeer = getCurrentSeerRating(lead);

  if (!tonnage || !currentSeer || newSeer <= currentSeer) return null;

  const currentAnnualCost = getAnnualCoolingCost(tonnage, currentSeer);
  const newAnnualCost = getAnnualCoolingCost(tonnage, newSeer);
  const annualSavings = Math.max(0, Math.round(currentAnnualCost - newAnnualCost));
  const percentSavings = Math.round(((currentAnnualCost - newAnnualCost) / currentAnnualCost) * 100);

  return {
    currentSeer,
    newSeer,
    currentAnnualCost: Math.round(currentAnnualCost),
    newAnnualCost: Math.round(newAnnualCost),
    annualSavings,
    percentSavings,
  };
}

function formatCurrencyCompact(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatTonnage(value: unknown) {
  const tonnage = parseNumber(value);
  if (!tonnage) return String(value ?? "Based on home size");
  return `${tonnage} Ton`;
}

function buildOptionMetrics(option: any, lead: Record<string, any>) {
  const systemType = getProductImageSystemType(lead.desiredSystemType);
  const tier = getPackageTier(option);

  if (systemType === "furnaceonly") {
    return [
      {
        label: "Heating efficiency",
        value: AFUE_RANGES[tier] ?? AFUE_RANGES.good,
      },
    ];
  }

  const seerRange = systemType === "minisplit" ? SEER_RANGES.miniSplit : SEER_RANGES[tier];
  const metrics: OptionMetric[] = [
    {
      label: "SEER range",
      value: seerRange.label,
      info: SEER_INFO_TEXT,
    },
  ];

  if (systemType === "centralheatairsystem" || systemType === "aconly") {
    metrics.push({
      label: "System size",
      value: formatTonnage(lead.currentACtonnage),
    });
  }

  if (systemType === "minisplit") {
    metrics.push({
      label: "Room/zone coverage",
      value: String(lead.miniSplitRooms ?? "Selected rooms"),
    });
  }

  const savings = getSeerSavings(lead, seerRange.estimateSeer);
  if (savings) {
    metrics.push({
      label: "Est. efficiency improvement",
      value: `${savings.percentSavings}%`,
    });
    metrics.push({
      label: "Est. annual savings",
      value: `${formatCurrencyCompact(savings.annualSavings)}/yr`,
    });
  }

  return metrics;
}

function buildOptionEnergySavings(option: any, lead: Record<string, any>): EnergySavingsEstimate | null {
  const systemType = getProductImageSystemType(lead.desiredSystemType);
  if (systemType === "furnaceonly") return null;

  const tier = getPackageTier(option);
  const seerRange = systemType === "minisplit" ? SEER_RANGES.miniSplit : SEER_RANGES[tier];
  const savings = getSeerSavings(lead, seerRange.estimateSeer);

  if (!savings) return null;

  return {
    ...savings,
    seerRange: seerRange.label,
    tonnage: formatTonnage(lead.currentACtonnage),
  };
}

function shouldRenderStep(step: any, lead: Record<string, any>) {
  if (!step.conditional) return true;

  const depValue = lead[step.conditional.dependsOn];
  if (!depValue) return false;

  if (step.conditional.valueIncludes && !hasValueMatch(depValue, step.conditional.valueIncludes)) {
    return false;
  }

  if (
    step.conditional.valueExcludes &&
    (hasValueMatch(depValue, step.conditional.valueExcludes) ||
      (normalizeForMatch(step.conditional.valueExcludes) === "furnace only" &&
        isFurnaceOnly(depValue)))
  ) {
    return false;
  }

  return true;
}

function getVisiblePages(lead: Record<string, any>) {
  const allPages = Array.from(new Set(steps.map((s) => s.renderOnPage))).sort(
    (a, b) => a - b,
  );

  return allPages.filter((pageIndex) =>
    steps.some((step) => step.renderOnPage === pageIndex && shouldRenderStep(step, lead)),
  );
}

function readSavedProgress(): SavedProgress | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed?.savedAt);

    if (!savedAt || Date.now() - savedAt > PROGRESS_TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return {
      page: Number.isFinite(parsed?.page) ? Number(parsed.page) : 0,
      pageId: Number.isFinite(parsed?.pageId) ? Number(parsed.pageId) : undefined,
      lead:
        parsed?.lead && typeof parsed.lead === "object"
          ? (parsed.lead as Record<string, any>)
          : {},
    };
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

const stepDescriptions: Record<string, string> = {
  homeType: "This helps us size labor and installation requirements.",
  squareFeet: "We use this to estimate cooling and heating demand.",
  stories: "Vertical layout affects system sizing.",
  yearBuilt: "Home age helps estimate insulation and efficiency needs.",
  ownership: "Ownership helps us tailor next steps and incentives.",
  desiredSystemType: "Choose the type of project you are considering.",
  desiredFuelType: "Fuel preference helps narrow your equipment options.",
  currentFuelType: "Current setup helps determine replacement complexity.",
  currentACtonnage: "Tonnage drives capacity matching and price accuracy.",
  systemlocation: "Location impacts install difficulty and timeline.",
  currentSystemAge: "System age helps us estimate urgency and rebates.",
  miniSplitRooms: "Room count helps estimate equipment and installation scope.",
};

function getRowColumns(row: Record<string, any>) {
  const values = Object.values(row ?? {});
  return {
    colA: String(values[0] ?? "").trim(),
    colB: String(values[1] ?? "").trim(),
    colC: String(values[2] ?? "").trim(),
  };
}

function getRowHeaders(row: Record<string, any>) {
  const headers = Object.keys(row ?? {});
  return {
    colA: String(headers[0] ?? "").trim(),
    colB: String(headers[1] ?? "").trim(),
    colC: String(headers[2] ?? "").trim(),
  };
}

function getQuestionMeta(
  rows: Record<string, any>[],
  fallbackTitle: string,
  fallbackDescription?: string,
) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      title: fallbackTitle,
      description: fallbackDescription,
    };
  }

  const firstRow = rows[0] ?? {};
  const { colB, colC } = getRowHeaders(firstRow);

  return {
    title: colB || fallbackTitle,
    description: colC || fallbackDescription,
  };
}

function getStepDescription(stepKey: string, description?: string) {
  if (stepKey === "yearBuilt") return undefined;
  if (stepKey !== "stories" || !description) return description;

  return description
    .replace(/\bduct routing and\s*/i, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

export default function App() {
  const [initialProgress] = useState(() => readSavedProgress());
  const [showRentNotice, setShowRentNotice] = useState(false);
  const [page, setPage] = useState(() => {
    const initialLead = initialProgress?.lead ?? {};
    const initialPages = getVisiblePages(initialLead);

    if (initialProgress?.pageId !== undefined) {
      const idx = initialPages.indexOf(initialProgress.pageId);
      if (idx >= 0) return idx;
    }

    if (typeof initialProgress?.page === "number") {
      return Math.max(0, Math.min(initialProgress.page, Math.max(0, initialPages.length - 1)));
    }

    return 0;
  });
  const [data, setData] = useState<any>({});
  const [pricing, setPricing] = useState<any[]>([]);
  const [lead, setLead] = useState<any>(initialProgress?.lead ?? {});
  const [processing, setProcessing] = useState(false);
  const visitorContextRef = useRef<VisitorContext>({ visitorIp: null });
  const appCopy = useMemo(() => resolveAppCopy(data.content), [data.content]);
  const pageSectionHeaders = useMemo<Record<number, string>>(
    () => ({
      1: appCopy.homeSectionTitle,
      2: appCopy.currentSystemSectionTitle,
      3: appCopy.desiredSystemSectionTitle,
    }),
    [appCopy],
  );
  const queryParams = useMemo(() => {
    if (typeof window === "undefined") return {};
    const params = new URLSearchParams(window.location.search);
    const entries = Array.from(params.entries());
    return entries.reduce((acc, [key, value]) => {
      const current = acc[key];

      if (current === undefined) {
        acc[key] = value;
      } else if (Array.isArray(current)) {
        acc[key] = [...current, value];
      } else {
        acc[key] = [current, value];
      }
      return acc;
    }, {} as Record<string, string | string[]>);
  }, []);

  /* ----------------------------------
   * Derive pages dynamically from steps
   * ---------------------------------- */
  const pages = useMemo(() => getVisiblePages(lead), [lead]);

  const totalPages = pages.length;
  const contactPageIndex = totalPages;
  const resultPageIndex = totalPages + 1;

  const canGoBack = page > 0 && !processing;
  const currentRenderPage = pages[page];

  const isContactFilled = CONTACT_FIELDS.every((field) =>
    String(lead[field.key] ?? "").trim().length > 0,
  );
  const isEmailFieldValid = isValidEmail(String(lead.contactEmail ?? ""));
  const isPhoneValid = onlyDigits(String(lead.contactPhone ?? "")).length === 10;
  const isContactComplete = isContactFilled && isEmailFieldValid && isPhoneValid;

  const contactErrors: Record<string, string> = {};
  if (String(lead.contactEmail ?? "").trim().length > 0 && !isEmailFieldValid) {
    contactErrors.contactEmail = "Enter a valid email address.";
  }
  if (String(lead.contactPhone ?? "").trim().length > 0 && !isPhoneValid) {
    contactErrors.contactPhone = "Enter a valid 10-digit phone number.";
  }

  function handleStepChange(stepKey: string, value: string) {
    if (stepKey === "ownership" && isRent(value)) {
      setShowRentNotice(true);
    }

    setLead((previous: any) => {
      const next = {
        ...previous,
        [stepKey]: value,
      };

      if (
        stepKey === "desiredSystemType" &&
        isFurnaceOnly(value)
      ) {
        delete next.currentACtonnage;
      }

      return next;
    });
  }

  function handleDetailsChange(field: string, value: string) {
    let nextValue = value;
    if (field === "contactPhone") {
      nextValue = formatPhone(value);
    } else if (field === "addressZip") {
      nextValue = formatZip(value);
    }

    setLead((previous: any) => ({
      ...previous,
      [field]: nextValue,
    }));
  }

  useEffect(() => {
    const maxPageIndex = resultPageIndex;
    if (page > maxPageIndex) {
      setPage(maxPageIndex);
    }
  }, [page, resultPageIndex]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, [page]);

  useEffect(() => {
    const progress = {
      page,
      pageId: pages[page] ?? pages[pages.length - 1] ?? -1,
      lead,
      savedAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  }, [page, pages, lead]);

  useEffect(() => {
    if (!lead.zipCode) return;
    if (String(lead.addressZip ?? "").trim().length > 0) return;

    setLead((previous: any) => ({
      ...previous,
      addressZip: previous.addressZip || previous.zipCode,
    }));
  }, [lead.zipCode, lead.addressZip]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const timer = window.setTimeout(() => {
      getVisitorContext(controller.signal).then((visitorContext) => {
        if (!cancelled) {
          visitorContextRef.current = visitorContext;
        }
      });
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, []);

  /* ----------------------------------
   * Fetch all sheets
   * ---------------------------------- */
  useEffect(() => {
    Promise.all(
      Object.entries(SHEETS).map(([k, url]) =>
        fetch(url)
          .then((r) => r.text())
          .then(parseCSV)
          .then((d) => [k, d]),
      ),
    ).then((results) => {
      const map: any = {};
      results.forEach(([k, v]: any) => (map[k] = v));
      setData(map);
      setPricing(map.pricing);
    });
  }, []);

  /* ----------------------------------
   * Steps for current page (with conditionals)
   * ---------------------------------- */
  const pageSteps = useMemo(() => {
    return steps.filter((step) => {
      if (step.renderOnPage !== pages[page]) return false;
      return shouldRenderStep(step, lead);
    });
  }, [page, pages, lead]);

  /* ----------------------------------
   * Page validation
   * ---------------------------------- */
  const isPageComplete = pageSteps.every((step) => lead[step.key]);

  /*
    AI AGENT NOTE (RE-ENABLE ADDRESS):
    Replace isPageComplete above with:

    const isAddressComplete = HOME_ADDRESS_FIELDS.every((field) =>
      String(lead[field.key] ?? "").trim().length > 0,
    );
    const addressZipDigitsLength = onlyDigits(String(lead.addressZip ?? "")).length;
    const isAddressZipValid = addressZipDigitsLength === 5 || addressZipDigitsLength === 9;
    const homeDetailsErrors: Record<string, string> = {};
    if (String(lead.addressZip ?? "").trim().length > 0 && !isAddressZipValid) {
      homeDetailsErrors.addressZip = "Enter a valid ZIP code (5 or 9 digits).";
    }
    const isPageComplete =
      pageSteps.every((step) => lead[step.key]) &&
      (currentRenderPage !== 1 || (isAddressComplete && isAddressZipValid));
  */

  /* ----------------------------------
   * Loading state
   * ---------------------------------- */
  if (pageSteps.some((s) => s.sheet && !data[s.sheet])) {
    return (
      <EstimatePage disclaimer={appCopy.estimateDisclaimer}>
        <div className="mx-auto flex min-h-[75vh] w-full max-w-xl items-center justify-center px-4">
          <p className="rounded-xl border border-[var(--line)] bg-white px-5 py-3 text-sm text-[var(--text-muted)] shadow-sm">
            Loading options...
          </p>
        </div>
      </EstimatePage>
    );
  }

  /* ----------------------------------
   * Artificial processing screen
   * ---------------------------------- */
  if (processing) {
    return (
      <EstimatePage disclaimer={appCopy.estimateDisclaimer}>
        <div className="mx-auto w-full max-w-xl px-4 py-6 sm:px-6 sm:py-8">
          <FakeProcessing
            onDone={() => {
              setProcessing(false);
              setPage(totalPages);
            }}
          />
        </div>
      </EstimatePage>
    );
  }

  /* ----------------------------------
   * Result page
   * ---------------------------------- */
  if (page === resultPageIndex) {
    const instructionResult = applySheetInstructionRules(lead, data, steps);

    const mergedLead = {
      ...lead,
      ...instructionResult.lead,
    };

    const effectiveLead = {
      ...mergedLead,
      desiredFuelType: resolveDesiredFuelType(mergedLead),
      currentACtonnage: inferTonnageFromSquareFeet(
        mergedLead,
        data.squareFeet,
      ),
    };
    const price = resolvePrice(effectiveLead, pricing);
    const recommendedOptions = getRecommendedOptions(effectiveLead, pricing, 3);
    const productImageUrl = getProductImageUrl(
      effectiveLead.desiredSystemType,
      data.productImages,
    );
    const recommendedOptionsWithImages = recommendedOptions.map((option) => ({
      ...option,
      imageUrl: productImageUrl,
      metrics: buildOptionMetrics(option, effectiveLead),
      energySavings: buildOptionEnergySavings(option, effectiveLead),
    }));
    const payload = buildLeadPayload(effectiveLead, price);

    return (
      <EstimatePage disclaimer={appCopy.estimateDisclaimer}>
        <div className="mx-auto max-w-xl p-4 sm:p-6">
          <Result
            payload={payload}
            options={recommendedOptionsWithImages}
            copy={{
              eyebrow: appCopy.resultsEyebrow,
              title: appCopy.resultsTitle,
              description: appCopy.resultsDescription,
              submitCta: appCopy.resultsSubmitCta,
              restartCta: appCopy.resultsRestartCta,
            }}
            onSubmit={async (selectedOption: any) => {
            const integrationPayload = {
              ...payload,
              queryParam: queryParams,
              queryParams,
              instructions: {
                appliedRules: instructionResult.appliedRules,
                displayNotes: instructionResult.displayNotes,
              },
              tracking: getTrackingPayload(visitorContextRef.current),
              pricing: {
                ...payload.pricing,
                basePrice: selectedOption?.price ?? payload.pricing?.basePrice ?? null,
                monthlyPrice: selectedOption?.monthlyPrice ?? null,
                startingPrice: selectedOption?.startingPrice ?? selectedOption?.rangeMin ?? null,
                startingMonthlyPrice:
                  selectedOption?.startingMonthlyPrice ?? selectedOption?.monthlyPrice ?? null,
                selectedOption: selectedOption?.title ?? null,
                selectedTag: selectedOption?.tag ?? null,
                productImageUrl: selectedOption?.imageUrl ?? null,
                metrics: selectedOption?.metrics ?? [],
                energySavings: selectedOption?.energySavings ?? null,
                rangeMin: selectedOption?.rangeMin ?? null,
                rangeMax: selectedOption?.rangeMax ?? null,
              },
              recommendations: recommendedOptionsWithImages.map((option) => ({
                title: option.title,
                tag: option.tag,
                price: option.price,
                monthlyPrice: option.monthlyPrice ?? null,
                startingPrice: option.startingPrice ?? option.rangeMin ?? null,
                startingMonthlyPrice:
                  option.startingMonthlyPrice ?? option.monthlyPrice ?? null,
                imageUrl: option.imageUrl ?? null,
                metrics: option.metrics ?? [],
                energySavings: option.energySavings ?? null,
                rangeMin: option.rangeMin ?? null,
                rangeMax: option.rangeMax ?? null,
              })),
            };

            const response = await fetch(ZAPIER_WEBHOOK_URL, {
              method: "POST",
              mode: "no-cors",
              headers: {
                "Content-Type": "text/plain;charset=UTF-8",
              },
              body: JSON.stringify(integrationPayload),
            });
            console.log("HVAC estimator payload sent (no-cors mode):", integrationPayload);
            console.log("Zapier response type:", response.type);
            window.location.assign(THANK_YOU_URL);
            }}
            onRestart={() => {
              localStorage.removeItem(STORAGE_KEY);
              setLead({});
              setProcessing(false);
              setPage(0);
            }}
          />
        </div>
      </EstimatePage>
    );
  }

  /* ----------------------------------
   * ZIP PAGE (special)
   * ---------------------------------- */
  if (pageSteps[0]?.type === "zip") {
    const zipOptions = data.zipCode
      .map((row: any) => {
        const value = Object.values(row)[0];
        return value ? String(value).replace(/\D/g, "") : "";
      })
      .filter((zip: string) => zip.length === 5);

    return (
      <EstimatePage disclaimer={appCopy.estimateDisclaimer}>
        <div className="mx-auto w-full max-w-xl px-4 pb-8 pt-5 sm:px-6">
          <StickyHeaderProgress step={page} total={totalPages} />

          <div className="rounded-3xl border border-[var(--line)] bg-white p-5 shadow-sm sm:p-6">
            <ZipCodeStep
              zipOptions={zipOptions}
              value={lead.zipCode}
              sectionTitle={appCopy.zipSectionTitle}
              title={appCopy.zipTitle}
              description={appCopy.zipDescription}
              onNext={(zip) => {
                setLead((l: any) => ({
                  ...l,
                  zipCode: zip,
                }));
                setPage((p) => p + 1);
              }}
            />
          </div>
        </div>
      </EstimatePage>
    );
  }

  if (page === contactPageIndex) {
    return (
      <EstimatePage disclaimer={appCopy.estimateDisclaimer}>
        <div className="mx-auto w-full max-w-xl px-4 pb-8 pt-5 sm:px-6">
          <StickyHeaderProgress
            step={totalPages - 1}
            total={totalPages}
            canGoBack={canGoBack}
            onBack={() => setPage((p) => Math.max(p - 1, 0))}
          />

          <div className="space-y-6 rounded-3xl border border-[var(--line)] bg-white p-5 shadow-sm sm:p-6">
            <DetailsFormStep
              title={appCopy.contactTitle}
              fields={CONTACT_FIELDS.map((field) => ({
                ...field,
                type: field.type ?? "text",
              }))}
              values={lead}
              onChange={handleDetailsChange}
              errors={contactErrors}
            />

            <button
              disabled={!isContactComplete}
              onClick={() => setPage(resultPageIndex)}
              className={`
              w-full rounded-xl py-3 font-semibold text-white transition
              ${
                isContactComplete
                  ? "bg-[var(--brand)] hover:bg-[var(--brand-dark)]"
                  : "cursor-not-allowed bg-gray-300"
              }
              `}
            >
              {appCopy.contactCta}
            </button>
          </div>
        </div>
      </EstimatePage>
    );
  }

  /* ----------------------------------
   * NORMAL PAGES
   * ---------------------------------- */
  return (
    <EstimatePage disclaimer={appCopy.estimateDisclaimer}>
      <div className="mx-auto w-full max-w-xl px-4 pb-8 pt-5 sm:px-6">
        <StickyHeaderProgress
          step={page}
          total={totalPages}
          canGoBack={canGoBack}
          onBack={() => setPage((p) => Math.max(p - 1, 0))}
        />

        <div className="space-y-6 rounded-3xl border border-[var(--line)] bg-white p-5 shadow-sm sm:p-6">
          {typeof currentRenderPage === "number" && pageSectionHeaders[currentRenderPage] && (
            <div className="rounded-xl border border-[var(--line)] bg-[var(--brand-soft)]/25 px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--brand)]">
                {pageSectionHeaders[currentRenderPage]}
              </p>
            </div>
          )}

        {/*
          AI AGENT NOTE (RE-ENABLE ADDRESS):
          Uncomment block below to show address form on Home Info stage.
        {currentRenderPage === 1 && (
          <DetailsFormStep
            title="Home details"
            description="Please provide your address so we can discuss your estimate."
            fields={HOME_ADDRESS_FIELDS.map((field) => ({
              ...field,
              type: field.key === "addressZip" ? "tel" : "text",
            }))}
            values={lead}
            onChange={handleDetailsChange}
            errors={homeDetailsErrors}
          />
        )}
        */}

          {pageSteps.map((stepConfig) => {
          const sheetRows = data[stepConfig.sheet] as Record<string, any>[];
          const rawOptions = sheetRows.map((o: any, i: number) => {
            const { colA } = getRowColumns(o);
            const value = String(colA);
            const iconDef = stepConfig.icons?.[i % stepConfig.icons.length];
            const isSelected = lead[stepConfig.key] === value;
            const normalizedValue = value.toLowerCase();
            const isDontKnowOption =
              normalizedValue.includes("i don't know") ||
              normalizedValue.includes("i dont know") ||
              normalizedValue.includes("not sure");

            let icon = null;

            if (stepConfig.key === "currentFuelType") {
              if (isDontKnowOption) {
                icon = <HelpCircle size={28} className="transition-colors" />;
              } else if (normalizedValue.includes("dual")) {
                icon = (
                  <div className="flex gap-1">
                    <Flame size={22} className="transition-colors" />
                    <Zap size={22} className="transition-colors" />
                  </div>
                );
              } else if (normalizedValue.includes("electric") || normalizedValue.includes("heat pump")) {
                icon = <Zap size={28} className="transition-colors" />;
              } else if (normalizedValue.includes("gas")) {
                icon = <Flame size={28} className="transition-colors" />;
              }
            } else if (stepConfig.key === "desiredFuelType") {
              if (isDontKnowOption) {
                icon = <HelpCircle size={28} className="transition-colors" />;
              } else if (normalizedValue.includes("dual")) {
                icon = (
                  <div className="flex gap-1">
                    <Flame size={22} className="transition-colors" />
                    <Zap size={22} className="transition-colors" />
                  </div>
                );
              } else if (normalizedValue.includes("electric") || normalizedValue.includes("heat pump")) {
                icon = <Zap size={28} className="transition-colors" />;
              } else if (normalizedValue.includes("gas")) {
                icon = <Flame size={28} className="transition-colors" />;
              }
            } else if (stepConfig.key === "miniSplitRooms") {
              icon = <MiniSplitRoomsIcon value={value} selected={isSelected} />;
            } else if (
              stepConfig.key === "systemlocation" &&
              normalizedValue.includes("attic")
            ) {
              icon = <AtticIcon size={28} className="transition-colors" />;
            } else if (Array.isArray(iconDef)) {
              icon = (
                <div className="flex gap-1">
                  {iconDef.map((Icon, idx) => (
                    <Icon key={idx} size={22} className="transition-colors" />
                  ))}
                </div>
              );
            } else if (iconDef) {
              const Icon = iconDef;
              icon = <Icon size={28} className="transition-colors" />;
            }

            return {
              value,
              label: value,
              icon,
            };
          });

          const squareFeetMeta = getQuestionMeta(
            sheetRows,
            stepConfig.title,
            "Pick the home size range that best matches your property.",
          );
          const yearBuiltMeta = getQuestionMeta(
            sheetRows,
            stepConfig.title,
          );
          const tonnageMeta = getQuestionMeta(
            sheetRows,
            stepConfig.title,
            `Select the closest tonnage. If unsure, choose "I don't know".`,
          );
          const systemAgeMeta = getQuestionMeta(
            sheetRows,
            stepConfig.title,
            "Choose the age range that best matches your current system.",
          );
          const defaultMeta = getQuestionMeta(
            sheetRows,
            stepConfig.title,
            stepDescriptions[stepConfig.key],
          );
          defaultMeta.description = getStepDescription(
            stepConfig.key,
            defaultMeta.description,
          );

          if (stepConfig.key === "squareFeet") {
            return (
              <TonnageSelectStep
                key={stepConfig.key}
                title={squareFeetMeta.title}
                options={rawOptions}
                value={lead[stepConfig.key]}
                description={squareFeetMeta.description}
                placeholder="Choose square footage range"
                icon={<Ruler size={18} />}
                onChange={(value) => handleStepChange(stepConfig.key, value)}
              />
            );
          }

          if (stepConfig.key === "yearBuilt") {
            return (
              <TonnageSelectStep
                key={stepConfig.key}
                title={yearBuiltMeta.title}
                options={rawOptions}
                value={lead[stepConfig.key]}
                description={getStepDescription(stepConfig.key, yearBuiltMeta.description)}
                placeholder="Choose year built"
                icon={<Calendar size={18} />}
                onChange={(value) => handleStepChange(stepConfig.key, value)}
              />
            );
          }

          if (stepConfig.key === "currentACtonnage") {
            return (
              <TonnageSelectStep
                key={stepConfig.key}
                title={tonnageMeta.title}
                options={rawOptions}
                value={lead[stepConfig.key]}
                description={tonnageMeta.description}
                placeholder="Choose tonnage"
                onChange={(value) => handleStepChange(stepConfig.key, value)}
              />
            );
          }

          if (stepConfig.key === "currentSystemAge") {
            return (
              <TonnageSelectStep
                key={stepConfig.key}
                title={systemAgeMeta.title}
                options={rawOptions}
                value={lead[stepConfig.key]}
                description={systemAgeMeta.description}
                placeholder="Choose system age"
                icon={<Clock3 size={18} />}
                onChange={(value) => handleStepChange(stepConfig.key, value)}
              />
            );
          }

          return stepConfig.largeCards ? (
            <LargeCardStep
              key={stepConfig.key}
              title={defaultMeta.title}
              description={defaultMeta.description}
              value={lead[stepConfig.key]}
              options={rawOptions}
              onChange={(value) => handleStepChange(stepConfig.key, value)}
            />
          ) : (
            <VisualStep
              key={stepConfig.key}
              title={defaultMeta.title}
              description={defaultMeta.description}
              value={lead[stepConfig.key]}
              options={rawOptions}
              onChange={(value) => handleStepChange(stepConfig.key, value)}
            />
          );
          })}

          <div className="sticky bottom-3 z-30 mt-2 bg-white/95 pb-1 pt-2 backdrop-blur supports-[backdrop-filter]:bg-white/90">
            <button
              disabled={!isPageComplete}
              className={`
                w-full rounded-xl py-3 font-semibold text-white transition
                ${
                  isPageComplete
                    ? "bg-[var(--brand)] shadow-lg shadow-[var(--brand)]/20 hover:bg-[var(--brand-dark)]"
                    : "bg-gray-300 cursor-not-allowed"
                }
              `}
              onClick={() => {
                if (!isPageComplete) return;

                if (page === totalPages - 1) {
                  setProcessing(true);
                } else {
                  setPage((p) => p + 1);
                }
              }}
            >
              Continue
            </button>
          </div>
        </div>
      </div>

      {showRentNotice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-3xl border border-[var(--line)] bg-white p-5 shadow-xl">
            <p className="text-sm font-semibold uppercase tracking-[0.12em] text-[var(--brand)]">
              Homeowner confirmation
            </p>
            <p className="mt-3 text-base leading-relaxed text-[var(--text-primary)]">
              {RENT_NOTICE}
            </p>
            <button
              type="button"
              className="mt-5 w-full rounded-xl bg-[var(--brand)] py-3 text-sm font-semibold text-white transition hover:bg-[var(--brand-dark)]"
              onClick={() => setShowRentNotice(false)}
            >
              I understand
            </button>
          </div>
        </div>
      )}
    </EstimatePage>
  );
}
